import { useQuery } from '@tanstack/react-query';
import {
  getCommunityDraft,
  getTheme,
  listMyThemes,
  listPresets,
  listPublicThemes,
  listRevisions,
} from '../api/themes';

/** Server state for theme documents — all reads live here (TanStack Query). */

export const themeKeys = {
  all: ['themes'] as const,
  mine: ['themes', 'mine'] as const,
  detail: (id: string) => ['themes', 'detail', id] as const,
  communityDraft: (communityId: string, ownerId: string) =>
    ['themes', 'community-draft', communityId, ownerId] as const,
  revisions: (id: string) => ['themes', 'revisions', id] as const,
  public: ['themes', 'public'] as const,
  presets: ['themes', 'presets'] as const,
};

export function useMyThemes(enabled: boolean) {
  return useQuery({ queryKey: themeKeys.mine, queryFn: listMyThemes, enabled });
}

export function useThemeRow(id: string | undefined) {
  return useQuery({
    queryKey: themeKeys.detail(id ?? ''),
    queryFn: () => getTheme(id!),
    enabled: Boolean(id),
  });
}

export function useCommunityDraft(communityId: string | undefined, ownerId: string | undefined) {
  return useQuery({
    queryKey: themeKeys.communityDraft(communityId ?? '', ownerId ?? ''),
    queryFn: () => getCommunityDraft(communityId!, ownerId!),
    enabled: Boolean(communityId && ownerId),
  });
}

export function useThemeRevisions(themeId: string | null, enabled = true) {
  return useQuery({
    queryKey: themeKeys.revisions(themeId ?? ''),
    queryFn: () => listRevisions(themeId!),
    enabled: Boolean(themeId) && enabled,
  });
}

export function usePublicThemes() {
  return useQuery({ queryKey: themeKeys.public, queryFn: () => listPublicThemes(), staleTime: 60_000 });
}

export function usePresets() {
  return useQuery({ queryKey: themeKeys.presets, queryFn: listPresets, staleTime: 5 * 60_000 });
}
